import React from 'react';
import { Grid, Typography } from '@mui/material';
import {
  Payments,
  PieChartOutline,
  EmojiEvents,
  Sell,
  Schedule,
  TrackChanges,
  Layers,
} from '@mui/icons-material';
import { StatCard } from '../../common/pageKit';
import { DeltaChip, formatINR, formatNumber } from '../../../dashboard/primitives';
import { SalesOverviewKpis } from '../../../types/salesAnalytics';

/**
 * The 7-KPI headline row from GET /sales-analytics/overview.
 *
 * Every value here can legitimately be null (see SalesAnalytics.js's header
 * comment) and StatCard already renders null as an em dash, so the only job
 * left for this component is explaining *why* — "0% win rate" and "no deals
 * closed yet" are different claims, and a bare dash without a reason reads as
 * a loading glitch rather than a fact about the period.
 */

interface KpiSpec {
  key: keyof SalesOverviewKpis;
  title: string;
  icon: React.ReactNode;
  color: string;
  format: (v: number) => string;
  nullReason: string;
  /** Down is good for this metric (none currently, kept for future metrics). */
  invert?: boolean;
}

const formatRatio = (v: number): string => `${v.toFixed(2)}×`;
const formatDays = (v: number): string => `${formatNumber(v)} d`;
const formatPctLocal = (v: number): string => `${v.toFixed(1)}%`;

const KPI_SPECS: KpiSpec[] = [
  {
    key: 'revenue',
    title: 'Revenue',
    icon: <Payments />,
    color: '#16a34a',
    format: (v) => formatINR(v, true),
    nullReason: 'No invoices in this period',
  },
  {
    key: 'grossMarginPct',
    title: 'Gross Margin %',
    icon: <PieChartOutline />,
    color: '#0284c7',
    format: formatPctLocal,
    nullReason: 'No cost-captured invoice lines yet',
  },
  {
    key: 'winRatePct',
    title: 'Win Rate',
    icon: <EmojiEvents />,
    color: '#8b5cf6',
    format: formatPctLocal,
    nullReason: 'No deals closed yet',
  },
  {
    key: 'avgDealSize',
    title: 'Avg Deal Size',
    icon: <Sell />,
    color: '#f59e0b',
    format: (v) => formatINR(v, true),
    nullReason: 'No deals won yet',
  },
  {
    key: 'avgSalesCycleDays',
    title: 'Avg Sales Cycle',
    icon: <Schedule />,
    color: '#06b6d4',
    format: formatDays,
    nullReason: 'No deals won yet',
  },
  {
    key: 'quotaAttainmentPct',
    title: 'Quota Attainment',
    icon: <TrackChanges />,
    color: '#ec4899',
    format: formatPctLocal,
    nullReason: 'No company quota set for this period',
  },
  {
    key: 'pipelineCoverageRatio',
    title: 'Pipeline Coverage',
    icon: <Layers />,
    color: '#6366f1',
    format: formatRatio,
    nullReason: 'No company quota set for this period',
  },
];

interface KpiGridProps {
  kpis: SalesOverviewKpis | null;
  comparing: boolean;
}

const KpiGrid: React.FC<KpiGridProps> = ({ kpis, comparing }) => (
  <Grid container spacing={2} sx={{ mb: 3 }}>
    {KPI_SPECS.map((spec) => {
      const kpi = kpis?.[spec.key];
      const value = kpi?.value ?? null;
      const displayValue = value === null ? null : spec.format(value);

      const footer =
        value === null ? (
          <Typography variant="caption" color="text.disabled">
            {spec.nullReason}
          </Typography>
        ) : comparing ? (
          <DeltaChip value={kpi?.changePct ?? null} invert={spec.invert} />
        ) : undefined;

      return (
        <Grid key={spec.key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
          <StatCard title={spec.title} value={displayValue} icon={spec.icon} color={spec.color} footer={footer} />
        </Grid>
      );
    })}
  </Grid>
);

export default KpiGrid;
